<template>
    <div class="page page--light">
        <section class="row gradient-background">
            <div class="wrapper wrapper--no-max-width">
                <header class="row__header">
                    <back-button :isAbsolute="false"
                        routeToHome
                        isBlack
                    />

                    <h1>{{ $t("predictors.sociosPredictor") }}</h1>
                </header>

                <div class="predictor">
                    <h1 class="predictor__heading">{{ $t("matches.titles.sport") }}</h1>

                    <matches-sports-tabs
                        :selectedKey="currentSelectedTabKey"
                        @selectedTabChanged="onTabChanged"
                    />

                    <h1 class="predictor__heading">{{ $t("matches.titles.team") }}</h1>

                    <template v-if="!isLoading">
                        <accordion-group v-if="isFootball">
                            <accordion-panel
                                v-for="(country, index) in getFilteredCountriesList"
                                :key="country.id"
                                :elementIndex="index"
                                :title="country.display"
                                :icon="countryLogoCheck(country)"
                            >
                                <article class="predictor-country__card">
                                    <matches-teams :teams="country.teams" />
                                </article>
                            </accordion-panel>
                        </accordion-group>

                        <div v-if="isAmericanFootball"
                            class="conferences"
                        >
                            <div
                                v-for="conferenceNFLName in conferencesNFL"
                                :key="conferenceNFLName"
                                class="conferences__item"
                            >
                                <span class="conferences__item-name">{{
                                    conferenceNFLName
                                }}</span>
                                <matches-teams
                                    :teams="conferencesTeamsNFL[conferenceNFLName]"
                                />
                            </div>
                        </div>

                        <div v-if="isSeason || isRugbyUnion"
                            class="conferences"
                        >
                            <div class="conferences__item">
                                <span
                                    v-if="!isRugbyUnion"
                                    class="conferences__item-name"
                                >
                                    {{ seasonName }}
                                </span>

                                <matches-teams :teams="isSeason ? selectedSeasonTeams : selectedRugbyTeams" />
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import {
    mapGetters, mapActions,
} from 'vuex';

import {
    SPORTS,
    SPORT_IDS,
} from '@shared/modules/fan-engagement/constants/sportEventSports.ts';

import {
    SEASONS,
    SEASON_IDS,
} from '@shared/modules/fan-engagement/constants/sportEventSeasons';

import backButton from '@webapp/components/back-button/back-button.vue';
import accordionGroup from '@webapp/components/accordion/accordion-group.vue';
import accordionPanel from '@webapp/components/accordion/accordion-panel.vue';
import matchesSportsTabs from '@webapp/modules/fan-engagement/components/matches/matches-sports-tabs.vue';
import matchesTeams from '@webapp/modules/fan-engagement/components/matches/matches-teams.vue';

export default {
    name: 'predictor-v2',
    components: {
        backButton,
        accordionGroup,
        accordionPanel,
        matchesSportsTabs,
        matchesTeams,
    },
    data() {
        return {
            predictorCountries: [],
            previouslyExpandedCountryIndex: -1,
        };
    },
    computed: {
        ...mapGetters('predictorTeams', [
            'isFootballCountriesLoading',
            'isAmericanFootballCountriesLoading',
            'isSeasonTeamsLoading',
            'isRugbyTeamsLoading',
            'getFilteredCountriesList',
            'getFilteredConferenceList',
            'selectedSeasonTeams',
            'selectedRugbyTeams',
        ]),

        ...mapGetters('predictorFilters', [
            'availableTabs',
            'currentSelectedTabKey',
            'selectedTab',
        ]),

        isLoading() {
            return (
                this.isFootballCountriesLoading ||
                this.isAmericanFootballCountriesLoading ||
                this.isSeasonTeamsLoading ||
                this.isRugbyTeamsLoading
            );
        },

        isFootball() {
            return this.currentSelectedTabKey === SPORTS[SPORT_IDS.FOOTBALL].key;
        },

        isRugbyUnion() {
            return this.currentSelectedTabKey === SPORTS[SPORT_IDS.RUGBY_UNION].key;
        },

        isAmericanFootball() {
            return this.currentSelectedTabKey === SPORTS[SPORT_IDS.AMERICAN_FOOTBALL].key;
        },

        isSeason() {
            return this.currentSelectedTabKey === SEASONS[SEASON_IDS.GLOBAL_TOURNAMENT].key;
        },

        seasonName() {
            return this.selectedTab && this.isSeason ? this.selectedTab.title : '';
        },

        conferencesNFL() {
            if (!this.getFilteredConferenceList) {
                return null;
            }

            return Object.keys(this.getFilteredConferenceList);
        },

        conferencesTeamsNFL() {
            return this.getFilteredConferenceList;
        },
    },
    async mounted() {
        await this.fetchSeasons();
        await this.fetchSports();
        await this.fetchTeamsBySeasonExternalId([SEASON_IDS.GLOBAL_TOURNAMENT]);
        await this.fetchFootballCountries();
        await this.fetchAmericanFootballCountries();
        await this.fetchRugbyUnionTeams();
    },
    methods: {
        ...mapActions('predictorTeams', [
            'fetchFootballCountries',
            'fetchAmericanFootballCountries',
            'fetchTeamsBySeasonExternalId',
            'fetchRugbyUnionTeams',
        ]),
        ...mapActions('predictorSports', ['fetchSports']),
        ...mapActions('predictorSeasons', ['fetchSeasons']),
        ...mapActions('predictorFilters', ['setCurrentSelectedTabKey']),

        onTabChanged({ newKey }) {
            this.setCurrentSelectedTabKey(newKey);
        },

        countryLogoCheck(country) {
            return country.logo
                ? country.logo
                : require('@webapp/assets/socios-grey-placeholder.webp');
        },
    },
};
</script>

<style lang="scss" scoped>
.predictor {
    padding: 0 5.6rem;
    box-sizing: border-box;

    @include max-width(md) {
        padding: 0 1rem;
    }

    &__heading {
        font-size: 1.8rem;
    }
}

.predictor-country {
    &__header {
        font-size: 1.2rem;
        font-weight: 400;
        margin: 0 0 0.7rem;
        font-family: "Nunito";
        color: $daintree-color;
    }
}

.predictor-league + .predictor-league {
    margin-top: 2rem;
}

.conferences {
    &__item {
        margin-bottom: 20px;
    }

    &__item-name {
        display: block;
        margin-bottom: 10px;
        font-size: 13px;
        line-height: 18px;
    }
}
</style>